import { useMutation } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

/**
 * Client-side abort timeout for the category-suggestions call. Kept snappy per
 * the handoff's "never make them wait" principle: a warm generation lands in
 * ~1-3s and cache hits in ~100ms, so 4s comfortably covers the common cases.
 * A genuine cold miss (~7s, a brand-new phrase) falls back gracefully here —
 * but the BACKEND keeps working its full 8s window and writes the cache row
 * even after this client aborts, so the phrase self-heals: every seller after
 * the first-timer (globally) gets it instantly. Tunable if backend timing
 * changes.
 */
export const SUGGEST_TIMEOUT_MS = 4000;

export type CategorySuggestion = { nameEn: string; nameAr: string };

export type CategorySuggestionsResult = {
  suggestions: CategorySuggestion[];
  cached: boolean;
};

export function useCategorySuggestionsMutation() {
  const mutation = useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ phrase, signal }: { phrase: string; signal?: AbortSignal }) =>
      apiJsonClient<CategorySuggestionsResult>(endpoints.seller.onboardingCategorySuggestions, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase }),
        signal,
      }),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
